import React, {useCallback, useContext, useEffect, useState} from "react";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import "../styles/FarmNFT.css";

import Math from "./utils/Math";
import Timestamp from "./utils/Timestamp";

import {Web3Context, ContractsContext} from "../contexts/Context";
import Button from "react-bootstrap/Button";

import ERC20_ABI from "../ABI/ERC20";
import BN from "bn.js";
import NFT from "./NFT";
import Address from "./common/Address";
import ERC20TokenInfo from "./ERC20TokenInfo";

import Logger from "../components/utils/Logger";

import {useToasts} from 'react-toast-notifications';
import Transaction from "./common/Transaction";

const FarmNFT = ({nft}) => {
  const [myBalance, setMyBalance] = useState("?");
  const [reloadBalance, setReloadBalance] = useState(false);
  const [tokenName, setTokenName] = useState("?");
  const [tokenSymbol, setTokenSymbol] = useState("?");
  const [tokenDecimals, setTokenDecimals] = useState("?");
  const [erc20Reward, setErc20Reward] = useState("?");
  const [enoughBalance, setEnoughBalance] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [txHash, setTxHash] = useState(null);

  const {addToast} = useToasts();

  const web3Context = useContext(Web3Context);
  const contractsContext = useContext(ContractsContext);

  const {web3, activeAccount} = web3Context;

  const {symbolERC20, decimalsERC20} = contractsContext;

  const showError = (content) => {
    addToast(content, {appearance: 'error'})
  };

  const showSuccess = (content) => {
    addToast(content, {appearance: 'success'})
  };

  const showInfo = useCallback((content) => {
    addToast(content, {appearance: 'info'})
  }, [addToast]);

  useEffect(() => {
    Logger.log("useEffect loadERC20");

    const loadERC20 = async () => {
      const contract = new web3.eth.Contract(ERC20_ABI, nft.tokenERC20);
      const balance = await contract.methods.balanceOf(activeAccount).call();
      Logger.log(`Account ${activeAccount} balance of token ${nft.tokenERC20} is ${balance}`);
      setMyBalance(balance);
      setReloadBalance(false);

      const name = await contract.methods.name().call();
      setTokenName(name);
      const symbol = await contract.methods.symbol().call();
      setTokenSymbol(symbol);
      const decimals = await contract.methods.decimals().call();
      setTokenDecimals(decimals);
      Logger.log(`Loaded token ${nft.tokenERC20}: ${name}, ${symbol}, ${decimals}`);
    };

    loadERC20();
  }, [reloadBalance, nft, web3, activeAccount]);

  useEffect(() => {
    Logger.log("useEffect loadBonusInfo");

    const loadBonusInfo = async () => {
      const {contractNiftyEggsFarmer} = contractsContext;
      if (contractNiftyEggsFarmer) {
        const reward = await contractNiftyEggsFarmer.methods.getERC20Reward(nft.tokenERC20, nft.lockPeriod).call();
        setErc20Reward(reward);
      }
    };

    loadBonusInfo();
  }, [nft, contractsContext, symbolERC20, decimalsERC20]);

  useEffect(() => {
    Logger.log("useEffect loadEnoughBalance");

    const loadEnoughBalance = async () => {
      if (!myBalance || myBalance === "?" || !nft.lockAmount) {
        setEnoughBalance(false);
        return;
      }
      const balanceBN = new BN(myBalance);
      const requiredBN = new BN(nft.lockAmount);
      setEnoughBalance(balanceBN.gte(requiredBN));
    };

    loadEnoughBalance();
  }, [myBalance, nft]);

  useEffect(() => {
    Logger.log("useEffect loadRemaining");

    const loadRemaining = async () => {
      if (nft) {
        const locked = nft.lockedSupply;
        const total = nft.totalSupply;
        if (total.toString() === "0") {
          setRemaining("∞");
          return;
        }
        const totalBN = new BN(total);
        const lockedBN = new BN(locked);
        setRemaining(totalBN.sub(lockedBN).toString());
      }
    };

    loadRemaining();
  }, [nft]);

  useEffect(() => {
    Logger.log("useEffect showTx");

    const showTx = async () => {
      if (txHash) {
        showInfo
        (
          <span>Transaction hash:
            <Transaction txHash={txHash} format={false}/>
          </span>
        );
      }
    };

    showTx();
  }, [txHash, showInfo]);

  const farmFunc = async () => {
    Logger.log("Farm func", nft);

    const {contractNiftyEggsFarmer} = contractsContext;

    try {
      const balance = new BN(myBalance);
      const lockAmount = new BN(nft.lockAmount);

      if (balance.lt(lockAmount)) {
        Logger.log("Insufficient token balance!");
        //alert("Insufficient token balance!");
        showError("Insufficient token balance!");
        return;
      }

      const contract = new web3.eth.Contract(ERC20_ABI, nft.tokenERC20);
      let allowance = await contract.methods.allowance(activeAccount, contractNiftyEggsFarmer._address).call();
      Logger.log(`Account ${activeAccount} allowance of token ${nft.tokenERC20} to ${contractNiftyEggsFarmer._address} is ${allowance}`);
      allowance = new BN(allowance);
      if (allowance.lt(lockAmount)) {
        Logger.log("Approving...");
        showInfo("First you need to approve tokens. This only needs to be done once for this token.");
        const UINT_MAX = "115792089237316195423570985008687907853269984665640564039457584007913129639935";
        const approved = await contract.methods.approve(contractNiftyEggsFarmer._address, UINT_MAX).send({
          from: activeAccount
        }, (err, txHash) => setTxHash(txHash));

        Logger.log("approved", approved);
        if (approved.status === true) {
          showInfo("Tokens approved. Now you need to confirm another transaction which starts the actual farming.");
        } else {
          showError("Transaction failed :(");
          return;
        }
      }

      const result = await contractNiftyEggsFarmer.methods.lock(nft.id).send({
        from: activeAccount
      }, (err, txHash) => setTxHash(txHash));

      Logger.log(result);
      if (result.status === true) {
        showSuccess("Success: you started farming NFT!");
      } else {
        showError("Transaction failed :(");
        return;
      }

      setReloadBalance(true);
    } catch (e) {
      Logger.error(e);
      showError("Error :(")
    }
  };

  const bonusIsHidden = (bonus) => {
    return bonus <= 0;
  };

  return (
    <Container className="container-farm">
      <Row className="nft-row mb-3">
        <Col xs="12" lg="3">
          <NFT id={nft.id} uri={nft.uri}/>
        </Col>
        <Col className="m-auto">
          <Container>
            <Row>
              <Col className="text-right px-0 m-auto">
                <div className="farm-titles">
                  <div><strong>ERC20 token</strong></div>
                  <div><strong>Remaining</strong></div>
                  <div><strong>Lock</strong></div>
                  <div><strong>Created by</strong></div>
                  <div className={bonusIsHidden(erc20Reward) ? 'd-none' : ''}><strong>Bonus reward</strong></div>
                  <div><strong>My balance</strong></div>
                </div>
              </Col>
              <Col className="text-left px-0 m-auto">
                <div className="farm-values">
                  <div><ERC20TokenInfo name={tokenName} symbol={tokenSymbol} address={nft.tokenERC20}/></div>
                  <div>{remaining} / {getNftsTotal(nft.totalSupply)}</div>
                  <div>{Math.formatDecimals(nft.lockAmount, tokenDecimals)} {tokenSymbol} for {Timestamp.unixTimestampToDays(nft.lockPeriod)}</div>
                  <div><Address address={nft.creator}/></div>
                  <div
                    className={bonusIsHidden(erc20Reward) ? 'd-none' : 'text-warning'}>{Math.formatDecimals(erc20Reward, decimalsERC20)} {symbolERC20}</div>
                  <div>{Math.formatDecimals(myBalance, tokenDecimals)} {tokenSymbol}</div>
                </div>
              </Col>
            </Row>
            <Row className="justify-content-center">
              <div>
                {
                  (remaining && (remaining > 0 || remaining === "∞")) ?
                    enoughBalance ?
                      <div className="d-flex mt-3 ml-auto justify-content-left align-items-center">
                        <Button className="btn-lg btn-scale" variant="primary"
                                onClick={() => farmFunc(nft)}>Farm!</Button>
                      </div>
                      :
                      <div>Not enough balance.</div>
                    :
                    <div>Out of stock!</div>
                }
              </div>
            </Row>
          </Container>
        </Col>
      </Row>
    </Container>
  )
};

const getNftsTotal = (total) => {
  if (total.toString() === "0") return "unlimited";
  return total;
};

export default FarmNFT;